/**
 * Hosting Admin Dashboard
 *
 * Admin interface for viewing all FluxNode hosting subscriptions.
 * Designed for the hosting provider to manage and deploy nodes.
 * Requires HOSTING_ADMIN_KEY to access.
 */

import { useState, useEffect, useCallback } from 'react';
import { Icons } from '../components/Icons';
import { StorageService } from '../services/storage';

// Types
interface HostingNode {
  id: string;
  subscription_id: string;
  tier: 'cumulus' | 'nimbus' | 'stratus';
  txhash: string;
  outidx: string;
  payment_address: string;
  ip: string | null;
  status: string;
  monthly_price: number;
  created_at: string;
  deployed_by_hoster: boolean;
  deployed_at: string | null;
  hoster_notes: string | null;
}

interface WalletCredential {
  id: string;
  subscription_id: string;
  wallet_address: string;
  zel_id: string;
  identity_key: string;
}

interface EmailProxy {
  id: string;
  subscription_id: string;
  proxy_email: string;
  real_email: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface HostingSubscription {
  id: string;
  order_id: string;
  customer_email: string;
  user_id: string | null;
  status: string;
  duration_months: number;
  start_date: string;
  end_date: string;
  monthly_total: number;
  total_paid: number;
  currency: string;
  cumulus_count: number;
  nimbus_count: number;
  stratus_count: number;
  created_at: string;
  nodes: HostingNode[];
  wallet_credentials: WalletCredential[];
  email_proxy: EmailProxy | null;
}

interface HostingStats {
  total_subscriptions: number;
  active_subscriptions: number;
  pending_subscriptions: number;
  expired_subscriptions: number;
  total_nodes: number;
  active_nodes: number;
  pending_nodes: number;
  total_revenue: number;
  monthly_revenue: number;
  by_tier: {
    cumulus: number;
    nimbus: number;
    stratus: number;
  };
  by_status: {
    active: number;
    pending: number;
    expired: number;
    cancelled: number;
    failed: number;
  };
}

// Colors
const STATUS_COLORS: Record<string, string> = {
  active: '#10b981',
  pending: '#f59e0b',
  pending_payment: '#f59e0b',
  expired: '#6b7280',
  cancelled: '#6b7280',
  failed: '#ef4444',
  deploying: '#3b82f6',
  offline: '#ef4444',
};

const TIER_COLORS: Record<string, { color: string; gradient: string }> = {
  cumulus: { color: '#3b82f6', gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)' },
  nimbus: { color: '#8b5cf6', gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' },
  stratus: { color: '#ec4899', gradient: 'linear-gradient(135deg, #ec4899, #db2777)' },
};

interface HostingAdminProps {
  adminKey?: string; // Optional: if provided, skip login screen
}

export function HostingAdmin({ adminKey: propAdminKey }: HostingAdminProps = {}) {
  // Auth state
  const [adminKey, setAdminKey] = useState(() =>
    propAdminKey || StorageService.get<string>('hosting_admin_key', '')
  );
  const [isAuthenticated, setIsAuthenticated] = useState(!!propAdminKey);
  const [authError, setAuthError] = useState<string | null>(null);

  // Data state
  const [stats, setStats] = useState<HostingStats | null>(null);
  const [subscriptions, setSubscriptions] = useState<HostingSubscription[]>([]);
  const [selectedSubscription, setSelectedSubscription] = useState<HostingSubscription | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // UI state
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [exporting, setExporting] = useState(false);

  // API helper
  const apiCall = useCallback(async (action: string, params: Record<string, string> = {}) => {
    const queryParams = new URLSearchParams({ action, ...params });
    const response = await fetch(`/api/hosting-rig/admin?${queryParams}`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminKey}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        setIsAuthenticated(false);
        throw new Error('Non autorisé');
      }
      const error = await response.json();
      throw new Error(error.error || 'Erreur API');
    }

    return response.json();
  }, [adminKey]);

  // Login handler
  const handleLogin = async () => {
    setLoading(true);
    setAuthError(null);

    try {
      await apiCall('stats');
      StorageService.set('hosting_admin_key', adminKey);
      setIsAuthenticated(true);
    } catch {
      setAuthError('Clé admin invalide');
    } finally {
      setLoading(false);
    }
  };

  // Load stats and subscriptions
  const loadData = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    try {
      const [statsData, subsData] = await Promise.all([
        apiCall('stats'),
        apiCall('subscriptions', {
          status: statusFilter,
          limit: '100',
          ...(searchQuery && { search: searchQuery }),
        }),
      ]);

      setStats(statsData);
      setSubscriptions(subsData.subscriptions || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, apiCall, statusFilter, searchQuery]);

  // Export data
  const handleExport = async (format: 'json' | 'csv') => {
    setExporting(true);
    try {
      if (format === 'json') {
        const data = await apiCall('export');
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `hosting-export-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const data = await apiCall('nodes-csv');
        const blob = new Blob([data.csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `hosting-nodes-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  // Toggle node expansion
  const toggleNodeExpand = (nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  // Toggle node deployment status
  const toggleNodeDeployment = async (nodeId: string, currentStatus: boolean) => {
    try {
      const response = await fetch('/api/hosting-rig/admin?action=update-node-deployment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminKey}`,
        },
        body: JSON.stringify({
          nodeId,
          deployed: !currentStatus,
        }),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la mise a jour');
      }

      // Update local state
      setSubscriptions(prev => prev.map(sub => ({
        ...sub,
        nodes: sub.nodes.map(node =>
          node.id === nodeId
            ? { ...node, deployed_by_hoster: !currentStatus, deployed_at: !currentStatus ? new Date().toISOString() : null }
            : node
        ),
      })));

      // Also update selected subscription if it exists
      if (selectedSubscription) {
        setSelectedSubscription(prev => prev ? {
          ...prev,
          nodes: prev.nodes.map(node =>
            node.id === nodeId
              ? { ...node, deployed_by_hoster: !currentStatus, deployed_at: !currentStatus ? new Date().toISOString() : null }
              : node
          ),
        } : null);
      }
    } catch (error) {
      console.error('Error updating deployment status:', error);
    }
  };

  // Auto-load on auth
  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated, loadData]);

  // Try auto-login with stored key
  useEffect(() => {
    if (adminKey && !isAuthenticated) {
      handleLogin();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mask email for privacy (show first 3 chars + ***)
  const maskEmail = (email: string | null) => {
    if (!email) return '-';
    const prefix = email.slice(0, 3);
    return `${prefix}***`;
  };

  // Format date
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Get time remaining
  const getTimeRemaining = (endDate: string | null) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    if (diff <= 0) return { days: 0, expired: true };
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    return { days, expired: false };
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--bg-primary)',
        padding: '20px',
      }}>
        <div style={{
          background: 'var(--card-bg)',
          borderRadius: '16px',
          padding: '40px',
          maxWidth: '400px',
          width: '100%',
          border: '1px solid var(--border)',
        }}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <Icons.Server size={32} style={{ color: 'white' }} />
            </div>
            <h1 style={{ fontSize: '24px', fontWeight: 600, color: 'var(--text-primary)' }}>
              Hosting Rig Admin
            </h1>
            <p style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>
              Tableau de bord pour l'hebergeur FluxNode
            </p>
          </div>

          <input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Cle admin"
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              background: 'var(--input-bg)',
              color: 'var(--text-primary)',
              fontSize: '14px',
              marginBottom: '16px',
              boxSizing: 'border-box',
            }}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
          />

          {authError && (
            <p style={{ color: '#ef4444', fontSize: '14px', marginBottom: '16px', textAlign: 'center' }}>
              {authError}
            </p>
          )}

          <button
            onClick={handleLogin}
            disabled={loading || !adminKey}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: 'none',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
              cursor: loading || !adminKey ? 'not-allowed' : 'pointer',
              opacity: loading || !adminKey ? 0.6 : 1,
            }}
          >
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1800px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Icons.Server size={28} style={{ color: '#8b5cf6' }} />
            Hosting Rig Admin
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            Gestion des abonnements et nodes FluxNode
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={() => handleExport('csv')}
            disabled={exporting}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              background: 'var(--card-bg)',
              color: 'var(--text-primary)',
              cursor: exporting ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
            }}
          >
            <Icons.Download size={16} />
            Export CSV
          </button>
          <button
            onClick={() => handleExport('json')}
            disabled={exporting}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              background: 'var(--card-bg)',
              color: 'var(--text-primary)',
              cursor: exporting ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
            }}
          >
            <Icons.Download size={16} />
            Export JSON
          </button>
          <button
            onClick={() => {
              setIsAuthenticated(false);
              StorageService.remove('hosting_admin_key');
            }}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              background: 'transparent',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Deconnexion
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: '16px',
          marginBottom: '24px',
        }}>
          <StatCard title="Abonnements" value={stats.total_subscriptions} icon={<Icons.CreditCard size={20} />} color="#8b5cf6" />
          <StatCard title="Actifs" value={stats.active_subscriptions} icon={<Icons.CheckCircle size={20} />} color="#10b981" />
          <StatCard title="En attente" value={stats.pending_subscriptions} icon={<Icons.Clock size={20} />} color="#f59e0b" />
          <StatCard title="Total Nodes" value={stats.total_nodes} icon={<Icons.Server size={20} />} color="#3b82f6" />
          <StatCard title="Nodes Actifs" value={stats.active_nodes} icon={<Icons.Activity size={20} />} color="#10b981" />
          <StatCard title="Cumulus" value={stats.by_tier.cumulus} icon={<Icons.Cloud size={20} />} color="#3b82f6" />
          <StatCard title="Nimbus" value={stats.by_tier.nimbus} icon={<Icons.Cloud size={20} />} color="#8b5cf6" />
          <StatCard title="Stratus" value={stats.by_tier.stratus} icon={<Icons.Cloud size={20} />} color="#ec4899" />
          <StatCard title="Revenu Total" value={`${stats.total_revenue.toFixed(0)}€`} icon={<Icons.DollarSign size={20} />} color="#10b981" />
          <StatCard title="Revenu/mois" value={`${stats.monthly_revenue.toFixed(0)}€`} icon={<Icons.TrendingUp size={20} />} color="#3b82f6" />
        </div>
      )}

      {/* Main content */}
      <div style={{ display: 'grid', gridTemplateColumns: selectedSubscription ? '450px 1fr' : '1fr', gap: '24px' }}>
        {/* Subscriptions list */}
        <div style={{
          background: 'var(--card-bg)',
          borderRadius: '12px',
          border: '1px solid var(--border)',
          overflow: 'hidden',
        }}>
          {/* Filters */}
          <div style={{ padding: '16px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
              {['all', 'active', 'pending_payment', 'expired'].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    background: statusFilter === status ? '#8b5cf6' : 'var(--hover-bg)',
                    color: statusFilter === status ? 'white' : 'var(--text-secondary)',
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  {status === 'all' ? 'Tous' : status === 'pending_payment' ? 'En attente' : status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher par order_id..."
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '8px',
                border: '1px solid var(--border)',
                background: 'var(--input-bg)',
                color: 'var(--text-primary)',
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* List */}
          <div style={{ maxHeight: '700px', overflowY: 'auto' }}>
            {loading && subscriptions.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                Chargement...
              </div>
            ) : subscriptions.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                Aucun abonnement
              </div>
            ) : (
              subscriptions.map((sub) => (
                <div
                  key={sub.id}
                  onClick={() => setSelectedSubscription(sub)}
                  style={{
                    padding: '16px',
                    borderBottom: '1px solid var(--border)',
                    cursor: 'pointer',
                    background: selectedSubscription?.id === sub.id ? 'var(--hover-bg)' : 'transparent',
                    transition: 'background 0.2s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: '4px',
                      background: (STATUS_COLORS[sub.status] || '#6b7280') + '20',
                      color: STATUS_COLORS[sub.status] || '#6b7280',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                    }}>
                      {sub.status}
                    </span>
                    <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
                      {formatDate(sub.created_at)}
                    </span>
                  </div>
                  <div style={{ marginBottom: '6px' }}>
                    <div style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {sub.email_proxy ? (
                        <>
                          <Icons.Mail size={12} style={{ color: sub.email_proxy.enabled ? '#10b981' : '#6b7280' }} />
                          <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{sub.email_proxy.proxy_email}</span>
                        </>
                      ) : (
                        <span style={{ color: 'var(--text-secondary)' }}>{maskEmail(sub.customer_email)}</span>
                      )}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>
                      ID: {sub.user_id || 'Non associe'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icons.Server size={12} />
                      {sub.nodes.length} node{sub.nodes.length > 1 ? 's' : ''}
                    </span>
                    <span>{sub.total_paid}€</span>
                    <span>{sub.duration_months} mois</span>
                    {sub.end_date && (
                      <span style={{
                        color: getTimeRemaining(sub.end_date)?.expired ? '#ef4444' :
                               (getTimeRemaining(sub.end_date)?.days || 0) <= 10 ? '#f59e0b' : '#10b981'
                      }}>
                        {getTimeRemaining(sub.end_date)?.expired ? 'Expire' :
                         `${getTimeRemaining(sub.end_date)?.days}j restants`}
                      </span>
                    )}
                  </div>
                  {/* Mini heatmap */}
                  <div style={{ display: 'flex', gap: '4px', marginTop: '10px', flexWrap: 'wrap' }}>
                    {sub.nodes.map((node) => {
                      const nodeColor = STATUS_COLORS[node.status] || '#6b7280';
                      const isDeployed = node.deployed_by_hoster;
                      return (
                        <div
                          key={node.id}
                          title={`${node.tier} - ${node.status}${isDeployed ? ' (Deploye)' : ' (Non deploye)'}`}
                          style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '4px',
                            background: `${nodeColor}40`,
                            border: `1px solid ${nodeColor}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '9px',
                            fontWeight: 700,
                            color: nodeColor,
                            position: 'relative',
                          }}
                        >
                          {node.tier.charAt(0).toUpperCase()}
                          {isDeployed && (
                            <span style={{
                              position: 'absolute',
                              top: '-4px',
                              right: '-4px',
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              background: '#10b981',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '7px',
                              color: 'white',
                            }}>
                              ✓
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Subscription detail */}
        {selectedSubscription && (
          <div style={{
            background: 'var(--card-bg)',
            borderRadius: '12px',
            border: '1px solid var(--border)',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{ padding: '20px', borderBottom: '1px solid var(--border)', background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(99, 102, 241, 0.05))' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Details de l'abonnement
                </h2>
                <button
                  onClick={() => setSelectedSubscription(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--text-secondary)',
                    padding: '4px',
                  }}
                >
                  <Icons.X size={20} />
                </button>
              </div>

              {/* Info grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                <InfoRow label="Email Contact" value={selectedSubscription.email_proxy?.proxy_email || maskEmail(selectedSubscription.customer_email)} mono />
                <InfoRow label="User ID" value={selectedSubscription.user_id || 'Non associe'} mono />
                <InfoRow label="Order ID" value={selectedSubscription.order_id} mono />
                <InfoRow label="Statut" value={selectedSubscription.status} color={STATUS_COLORS[selectedSubscription.status]} />
                <InfoRow label="Duree" value={`${selectedSubscription.duration_months} mois`} />
                <InfoRow label="Debut" value={formatDate(selectedSubscription.start_date)} />
                <InfoRow label="Fin" value={formatDate(selectedSubscription.end_date)} />
                <InfoRow label="Total paye" value={`${selectedSubscription.total_paid}€`} color="#10b981" />
                <InfoRow label="Mensuel" value={`${selectedSubscription.monthly_total}€/mois`} />
              </div>

              {/* Email Proxy Section */}
              <div style={{
                marginTop: '16px',
                padding: '12px',
                borderRadius: '8px',
                background: 'rgba(139, 92, 246, 0.1)',
                border: '1px solid rgba(139, 92, 246, 0.2)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icons.Mail size={16} style={{ color: '#8b5cf6' }} />
                    <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>
                      Email Proxy
                    </span>
                  </div>
                  {selectedSubscription.email_proxy ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        padding: '3px 8px',
                        borderRadius: '4px',
                        background: selectedSubscription.email_proxy.enabled ? '#10b98120' : '#6b728020',
                        color: selectedSubscription.email_proxy.enabled ? '#10b981' : '#6b7280',
                        fontSize: '11px',
                        fontWeight: 600,
                      }}>
                        {selectedSubscription.email_proxy.enabled ? 'ACTIF' : 'DESACTIVE'}
                      </span>
                      <button
                        onClick={async () => {
                          try {
                            const response = await fetch('/api/hosting-rig/admin?action=toggle-email-proxy', {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${adminKey}`,
                              },
                              body: JSON.stringify({
                                subscriptionId: selectedSubscription.id,
                                enabled: !selectedSubscription.email_proxy?.enabled,
                              }),
                            });
                            if (response.ok) {
                              // Reload data
                              loadData();
                            }
                          } catch (e) {
                            console.error('Error toggling proxy:', e);
                          }
                        }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          background: selectedSubscription.email_proxy.enabled ? '#ef444420' : '#10b98120',
                          color: selectedSubscription.email_proxy.enabled ? '#ef4444' : '#10b981',
                          fontSize: '11px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        {selectedSubscription.email_proxy.enabled ? 'Desactiver' : 'Activer'}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={async () => {
                        try {
                          const response = await fetch('/api/hosting-rig/admin?action=create-email-proxy', {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${adminKey}`,
                            },
                            body: JSON.stringify({
                              subscriptionId: selectedSubscription.id,
                            }),
                          });
                          if (response.ok) {
                            loadData();
                          }
                        } catch (e) {
                          console.error('Error creating proxy:', e);
                        }
                      }}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: 'none',
                        background: '#8b5cf620',
                        color: '#8b5cf6',
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Creer Email Proxy
                    </button>
                  )}
                </div>
                {selectedSubscription.email_proxy && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                    {selectedSubscription.email_proxy.proxy_email}
                  </div>
                )}
              </div>
            </div>

            {/* Nodes */}
            <div style={{ padding: '20px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px', color: 'var(--text-primary)' }}>
                Nodes ({selectedSubscription.nodes.length})
              </h3>

              {/* Legend */}
              <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: '12px', height: '12px', borderRadius: '4px', background: '#10b981' }} />
                  Deploye
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: '12px', height: '12px', borderRadius: '4px', background: '#ef4444' }} />
                  Non deploye
                </span>
              </div>

              {/* Heatmap */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginBottom: '20px',
                padding: '16px',
                background: 'rgba(0,0,0,0.2)',
                borderRadius: '12px',
              }}>
                {selectedSubscription.nodes.map((node) => {
                  const isExpanded = expandedNodes.has(node.id);
                  const isDeployed = node.deployed_by_hoster;
                  const deployColor = isDeployed ? '#10b981' : '#ef4444';
                  const tierStyle = TIER_COLORS[node.tier] || TIER_COLORS.cumulus;

                  return (
                    <button
                      key={node.id}
                      onClick={() => toggleNodeExpand(node.id)}
                      title={`${node.tier} - ${node.ip || 'No IP'}${isDeployed ? ' (Deploye)' : ' (Non deploye)'}`}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        border: isExpanded ? `2px solid ${deployColor}` : `2px solid ${deployColor}40`,
                        background: isExpanded ? deployColor : `${deployColor}20`,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                        boxShadow: isExpanded ? `0 0 12px ${deployColor}50` : 'none',
                        position: 'relative',
                      }}
                    >
                      <span style={{
                        fontSize: '12px',
                        fontWeight: 700,
                        color: isExpanded ? 'white' : deployColor,
                        textTransform: 'uppercase',
                      }}>
                        {node.tier.charAt(0)}
                      </span>
                      {isDeployed && (
                        <span style={{
                          position: 'absolute',
                          top: '-4px',
                          right: '-4px',
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          background: '#10b981',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '8px',
                          color: 'white',
                          border: '1px solid rgba(0,0,0,0.2)',
                        }}>
                          ✓
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Expanded node details */}
              {selectedSubscription.nodes.filter(n => expandedNodes.has(n.id)).map((node) => {
                const tierStyle = TIER_COLORS[node.tier] || TIER_COLORS.cumulus;
                const credential = selectedSubscription.wallet_credentials.find(
                  c => c.wallet_address === node.payment_address
                );

                return (
                  <div
                    key={`detail-${node.id}`}
                    style={{
                      background: 'rgba(0,0,0,0.3)',
                      borderRadius: '12px',
                      border: `1px solid ${tierStyle.color}40`,
                      padding: '16px',
                      marginBottom: '12px',
                    }}
                  >
                    {/* Node Header */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '16px',
                      paddingBottom: '12px',
                      borderBottom: '1px solid rgba(255,255,255,0.1)',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: tierStyle.gradient,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <span style={{ color: 'white', fontSize: '14px', fontWeight: 700 }}>
                            {node.tier.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div style={{ fontSize: '15px', fontWeight: 600, color: 'white', textTransform: 'capitalize' }}>
                            {node.tier}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                            {node.monthly_price}€/mois
                          </div>
                        </div>
                      </div>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '20px',
                        background: (STATUS_COLORS[node.status] || '#6b7280') + '20',
                        color: STATUS_COLORS[node.status] || '#6b7280',
                        fontSize: '11px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                      }}>
                        {node.status}
                      </span>
                    </div>

                    {/* Node Details Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                      <DetailBox label="IP" value={node.ip || 'Non assigne'} />
                      <DetailBox label="Output Index" value={node.outidx} />
                      <DetailBox label="Txhash" value={node.txhash} mono fullWidth />
                      <DetailBox label="Payment Address" value={node.payment_address} mono fullWidth />
                      <DetailBox
                        label="Zel ID"
                        value={credential?.zel_id || 'Non configure'}
                        warn={!credential?.zel_id}
                        fullWidth
                      />
                      <DetailBox
                        label="Identity Key"
                        value={credential?.identity_key || 'Non configure'}
                        warn={!credential?.identity_key}
                        fullWidth
                      />
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                      {/* Toggle deployment status */}
                      <button
                        onClick={() => toggleNodeDeployment(node.id, node.deployed_by_hoster)}
                        style={{
                          flex: 1,
                          padding: '12px',
                          borderRadius: '8px',
                          border: 'none',
                          background: node.deployed_by_hoster
                            ? 'linear-gradient(135deg, #10b981, #059669)'
                            : 'linear-gradient(135deg, #ef4444, #dc2626)',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          transition: 'all 0.2s',
                        }}
                      >
                        {node.deployed_by_hoster ? (
                          <>
                            <Icons.CheckCircle size={16} />
                            Deploye
                          </>
                        ) : (
                          <>
                            <Icons.X size={16} />
                            Non deploye
                          </>
                        )}
                      </button>

                      {/* Copy all button */}
                      <button
                        onClick={() => {
                          const text = `Tier: ${node.tier}
Txhash: ${node.txhash}
Outidx: ${node.outidx}
Payment Address: ${node.payment_address}
Zel ID: ${credential?.zel_id || 'N/A'}
Identity Key: ${credential?.identity_key || 'N/A'}`;
                          navigator.clipboard.writeText(text);
                        }}
                        style={{
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid rgba(255,255,255,0.1)',
                          background: 'rgba(255,255,255,0.05)',
                          color: 'var(--text-secondary)',
                          cursor: 'pointer',
                          fontSize: '13px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <Icons.Copy size={14} />
                        Copier
                      </button>
                    </div>

                    {/* Deployment info */}
                    {node.deployed_at && (
                      <div style={{
                        marginTop: '8px',
                        fontSize: '11px',
                        color: 'var(--text-tertiary)',
                        textAlign: 'center',
                      }}>
                        Deploye le {new Date(node.deployed_at).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Show all nodes if none expanded */}
              {expandedNodes.size === 0 && (
                <p style={{ color: 'var(--text-secondary)', fontSize: '13px', textAlign: 'center' }}>
                  Cliquez sur un node pour voir ses details
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Stat card component
function StatCard({ title, value, icon, color }: { title: string; value: number | string; icon: React.ReactNode; color?: string }) {
  return (
    <div style={{
      background: 'var(--card-bg)',
      borderRadius: '12px',
      padding: '16px',
      border: '1px solid var(--border)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '8px',
          background: (color || '#6366f1') + '20',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: color || '#6366f1',
        }}>
          {icon}
        </div>
        <div>
          <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-primary)' }}>
            {value}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            {title}
          </div>
        </div>
      </div>
    </div>
  );
}

// Info row component
function InfoRow({ label, value, color, mono }: { label: string; value: string; color?: string; mono?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '4px', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{
        fontSize: '13px',
        color: color || 'var(--text-primary)',
        fontFamily: mono ? 'monospace' : 'inherit',
        wordBreak: 'break-all',
      }}>
        {value}
      </div>
    </div>
  );
}

// Detail box component
function DetailBox({ label, value, mono, fullWidth, warn }: { label: string; value: string; mono?: boolean; fullWidth?: boolean; warn?: boolean }) {
  return (
    <div style={{
      padding: '10px 12px',
      borderRadius: '8px',
      background: 'rgba(255,255,255,0.04)',
      gridColumn: fullWidth ? 'span 2' : undefined,
    }}>
      <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginBottom: '4px', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{
        fontSize: '12px',
        fontFamily: mono ? 'monospace' : 'inherit',
        color: warn ? '#f59e0b' : 'white',
        wordBreak: 'break-all',
      }}>
        {value}
      </div>
    </div>
  );
}

export default HostingAdmin;
